import React, { createContext, useState, ReactNode, useCallback } from 'react';

type EditActionType = 'OPEN' | 'CLOSE' | 'IDLE';

interface ProfileEditContextType {
  globalEditAction: { type: EditActionType; count: number };
  toggleAllEdits: () => void;
  registerEditState: (sectionId: string, isEditing: boolean) => void;
  isAnyEditing: boolean;
  isBulkEditing: boolean;
}

export const ProfileEditContext = createContext<ProfileEditContextType>({
  globalEditAction: { type: 'IDLE', count: 0 },
  toggleAllEdits: () => {},
  registerEditState: () => {},
  isAnyEditing: false,
  isBulkEditing: false,
});

export const ProfileEditProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [globalEditAction, setGlobalEditAction] = useState<{ type: EditActionType; count: number }>(
    { type: 'IDLE', count: 0 },
  );
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());

  const registerEditState = useCallback((sectionId: string, isEditing: boolean) => {
    setEditingSections((prev) => {
      const next = new Set(prev);
      if (isEditing) next.add(sectionId);
      else next.delete(sectionId);
      return next;
    });
  }, []);

  const isAnyEditing = editingSections.size > 0;
  const isBulkEditing = isAnyEditing;

  const toggleAllEdits = () => {
    const willOpen = !isAnyEditing;
    setGlobalEditAction((prev) => ({
      type: willOpen ? 'OPEN' : 'CLOSE',
      count: prev.count + 1,
    }));
  };

  return (
    <ProfileEditContext.Provider
      value={{ globalEditAction, toggleAllEdits, registerEditState, isAnyEditing, isBulkEditing }}
    >
      {children}
    </ProfileEditContext.Provider>
  );
};
